// Tranche AH pass 1: event detail page.
//
// Events are immutable — there are no dangerous actions. The page is a
// permalink target for cross-referencing from audit, journey timelines,
// and outbox-job inspections.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AuditFeed,
  Button,
  ControllerIdsPanel,
  EntityDetailLayout,
  Icon,
  Spinner,
  type AuditEntry,
} from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { fetchAuditForResource } from '../lib/detailFetchers.js';
import { useSiteMode } from '../auth/SiteModeContext.js';
import { humanizeAuditCode } from '../lib/auditLabels.js';
import { SnapshotImage } from '../components/SnapshotImage.js';

interface PortalEventRow {
  id: string;
  tenant_id: string;
  event_type: string;
  occurred_at: string;
  source_kind?: string | null;
  source_id?: string | null;
  unifi_user_id?: string | null;
  door_id?: string | null;
  payload?: unknown;
  snapshot?: {
    id: string;
    storage_key: string;
    captured_at: string;
  } | null;
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const siteMode = useSiteMode();
  const [event, setEvent] = useState<PortalEventRow | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setEvent(null);
    setError(null);
    Promise.all([
      apiClient.get<{ data: PortalEventRow }>(`/api/v1/events/${id}`),
      fetchAuditForResource('event', id, 10),
    ])
      .then(([e, a]) => {
        if (cancelled) return;
        setEvent(e.data);
        setAudit(a);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load event');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/activity')}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Activity
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const safeActions = (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          void (async () => {
            try {
              await navigator.clipboard.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              // ignore
            }
          })();
        }}
      >
        <Icon name="link" className="text-sm" />
        <span className="ml-1">{copied ? 'Copied' : 'Copy permalink'}</span>
      </Button>
      {/* UX-012/UX-014: this pointed at /activity?event_type=<type>. /activity
          is the management audit log, not the access-event stream, and it has
          no event_type parameter — so the link sent an access event to the
          wrong surface and filtered nothing when it got there. Access events
          live on Journeys. */}
      <Link
        to="/journeys"
        className="inline-flex items-center px-3 h-10 rounded-md bg-surface-container text-on-surface hover:bg-surface-container-high font-medium text-sm"
      >
        <Icon name="event" className="text-sm mr-1" /> Access journeys
      </Link>
    </>
  );

  // Multi-family PII gate (Round 1 review fix). The events endpoint
  // returns `payload` verbatim and may include resident attributes
  // (names, unit numbers, credential ids) until the controller-side
  // redactor in `GET /api/v1/admin/events/:id` ships. For commercial
  // and single-tenant installs the payload is operator-only and emitted
  // from the controller without resident attributes, so we keep the
  // existing verbatim render. For `multi_family`, we omit the payload
  // and direct the operator to the redacted admin route instead.
  const hidePayload = siteMode === 'multi_family';

  return (
    <EntityDetailLayout
      title={humanizeAuditCode(event.event_type)}
      subtitle={new Date(event.occurred_at).toLocaleString()}
      backHref="/activity"
      backLabel="Activity"
      safeActions={safeActions}
      related={
        <ControllerIdsPanel
          ids={[
            { label: 'Source', value: event.source_kind ? `${event.source_kind}:${event.source_id ?? ''}` : null },
            { label: 'Door ID', value: event.door_id ?? null },
            { label: 'Controller user ID', value: event.unifi_user_id ?? null },
          ]}
        />
      }
      audit={<AuditFeed entries={audit} label="Recent activity" />}
    >
      {event.snapshot && (
        <section className="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 mb-4">
          <div className="mb-3">
            <span className="text-xs font-semibold uppercase text-on-surface-variant tracking-wide">
              Access photo
            </span>
            <span className="ml-2 text-xs text-on-surface-variant">
              · {new Date(event.snapshot.captured_at).toLocaleString()}
            </span>
          </div>
          <SnapshotImage
            snapshotId={event.snapshot.id}
            alt="Access event camera capture"
            className="w-full max-w-lg h-64"
          />
        </section>
      )}
      <section
        aria-label="Event"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
      >
        <KV k="Event type" v={<>
          <div>{humanizeAuditCode(event.event_type)}</div>
          <p className="text-xs text-on-surface-variant font-mono mt-1">Code: {event.event_type}</p>
        </>} />
        <KV k="Occurred at" v={new Date(event.occurred_at).toLocaleString()} />
        <KV k="Tenant id" v={<code className="font-code text-code">{event.tenant_id}</code>} />
        <KV k="Source" v={event.source_kind ?? '—'} />
      </section>
      <section
        aria-label="Payload"
        className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6 space-y-3"
        data-testid="event-payload-section"
      >
        <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">
          Payload
        </h2>
        {hidePayload ? (
          <div
            role="note"
            data-testid="event-payload-multifamily-placeholder"
            className="font-body-md text-body-md text-on-surface-variant bg-surface-container rounded p-3"
          >
            <p>
              Event payload omitted from this view in multi-family mode to
              protect resident PII. Open via the admin API for the redacted
              form:
            </p>
            <p className="mt-2">
              <code className="font-code text-code">
                GET /api/v1/admin/events/{event.id}
              </code>
            </p>
          </div>
        ) : (
          <pre
            data-testid="event-payload-pre"
            className="font-code text-code bg-surface-container rounded p-3 overflow-x-auto"
          >
            {JSON.stringify(event.payload ?? {}, null, 2)}
          </pre>
        )}
      </section>
    </EntityDetailLayout>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}
